import type React from 'react';
import type { DashboardWidgetType, WidgetConfig, WidgetSpecificConfig } from '@trinity/chat/types/dashboard-config';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import type { ZodSchema } from 'zod';

// ─── Widget Render Props ────────────────────────────────────────────────────

/**
 * WidgetRenderProps — passed by the layout engine to each widget's `render`
 * function. The engine resolves data from `DashboardDataContext` so widgets
 * remain decoupled from context.
 *
 * Generic over `TType` so render functions can receive pre-narrowed config and
 * data types (e.g. `WidgetRenderProps<'chart'>` narrows config to the chart
 * variant and data to `{ type: 'chart'; data: ChartWidgetData } | null`).
 * Defaults to the full union when no type parameter is supplied.
 */
export interface WidgetRenderProps<TType extends DashboardWidgetType = DashboardWidgetType> {
  widgetId: string;
  /** The serialized config for this widget — controls appearance / settings */
  config: Extract<WidgetConfig, { type: TType }>;
  /** Resolved data from the dashboard data context (null when loading / unavailable) */
  data: Extract<CanonicalWidgetData, { type: TType }> | null;
  /** Grid column-units wide (1–3) */
  width: number;
  /** Grid row-units tall */
  height: number;
}

// ─── Registry Entry ───────────────────────────────────────────────────────────

export type WidgetCategory = 'visualization' | 'content' | 'metric' | 'custom';

/**
 * `TConfig` is the widget-specific config type (e.g. `ChartWidgetConfig`).
 * `TType` is the widget type literal (e.g. `'chart'`). When provided, the
 * `render` function receives pre-narrowed `WidgetRenderProps<TType>`.
 *
 * Both default to their full unions so the registry's internal Map can hold
 * mixed entries after type erasure.
 */
export interface WidgetRegistryEntry<
  TConfig extends WidgetSpecificConfig = WidgetSpecificConfig,
  TType extends DashboardWidgetType = DashboardWidgetType,
> {
  /** Unique type ID — must match `WidgetConfig.type` values */
  type: TType | (string & {});
  /** Human-readable label shown in the widget palette */
  label: string;
  /** Tooltip / description shown in the widget palette */
  description?: string;
  /** Icon component shown in the palette */
  icon?: React.ComponentType;
  category: WidgetCategory;
  /**
   * Render function called by the canvas/layout engine.
   * Receives resolved data + config — no context dependency.
   * Returns the React element for this widget.
   */
  render: (props: WidgetRenderProps<TType>) => React.ReactElement;
  /** Zod schema used to validate the widget's `config` object */
  configSchema: ZodSchema;
  /** Initial config populated when the widget is added to the canvas */
  defaultConfig: TConfig;
  /**
   * Returns an example JSON object showing the exact shape an HTTP agent must
   * return for this widget type to render correctly. Used in the Data tab to
   * show the user the expected response format.
   *
   * Receives the widget's current config so chart widgets can tailor the
   * example to the specific chart sub-type (bar vs pie vs stacked-bar, etc.).
   */
  getAgentDataExample?: (config: TConfig) => CanonicalWidgetData;
  /** Minimum grid width (columns). Defaults to 1. */
  minW?: number;
  /** Minimum grid height (rows). Defaults to 1. */
  minH?: number;
  /** Maximum grid width (columns). Defaults to 3. */
  maxW?: number;
  /** Maximum grid height (rows). No default — unlimited. */
  maxH?: number;
  /** When true, the widget cell border is hidden in the builder grid. */
  noBorder?: boolean;
  /**
   * Compute the optimal rowSpan for this widget based on its data.
   * Called after data is fetched or static data is committed so the block
   * auto-sizes to fit the content. Returns undefined to skip auto-sizing.
   */
  autoRowSpan?: (data: CanonicalWidgetData) => number | undefined;
}

// ─── Registry Class ───────────────────────────────────────────────────────────

/**
 * WidgetRegistry — the single source of truth for which widget types are
 * available in a dashboard.
 *
 * - The builder reads `getAll()` to populate the widget palette.
 * - The renderer calls `get(type).render(props)` to render the widget.
 * - Apps can extend the default registry with custom widget types via `register()`.
 */
export class WidgetRegistry {
  private readonly entries: Map<string, WidgetRegistryEntry> = new Map();

  /**
   * Register a new widget type. Overwrites any existing entry with the same type.
   *
   * Accepts a strongly-typed `WidgetRegistryEntry<TConfig>` at the call site for
   * write-time safety, then erases `TConfig` to the union base for internal storage
   * (necessary because function parameters are contravariant under `strictFunctionTypes`).
   */
  register<TConfig extends WidgetSpecificConfig, TType extends DashboardWidgetType = DashboardWidgetType>(entry: WidgetRegistryEntry<TConfig, TType>): void {
    // TConfig/TType → base union erasure: safe because the layout engine
    // passes the full WidgetConfig + CanonicalWidgetData unions at call time.
    this.entries.set(entry.type as string, entry as unknown as WidgetRegistryEntry);
  }

  /** Remove a widget type from the registry. */
  unregister(type: string): void {
    this.entries.delete(type);
  }

  /** Look up a widget type by ID. Returns `undefined` if not found. */
  get(type: string): WidgetRegistryEntry | undefined {
    return this.entries.get(type);
  }

  /** Return all registered widget types — used to populate the widget palette. */
  getAll(): WidgetRegistryEntry[] {
    return Array.from(this.entries.values());
  }

  /** Filter by category — e.g. 'visualization', 'metric'. */
  getByCategory(category: WidgetCategory): WidgetRegistryEntry[] {
    return this.getAll().filter(e => e.category === category);
  }

  /** Check whether a type is registered. */
  has(type: string): boolean {
    return this.entries.has(type);
  }
}
